<template>
  <v-container fill-height fluid>
    <!-- Date Filtering  -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      :timeout="timeout"
    >
      {{ snackbarMessage }}
    </v-snackbar>
    <v-row v-if="$isSectionEnabled('insights','personalizer_stats')">
      <v-col cols="12" xs="12" md="4">
        <v-menu
          ref="menuDates"
          v-model="menuDates"
          :close-on-content-click="false"
          :return-value.sync="dates"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeText"
              :placeholder="
                $vuetify.lang.t('$vuetify.common.daterange_placeholder_txt')
              "
              prepend-icon="mdi-calendar"
              readonly
              outlined
              dense
              hide-details="auto"
              v-bind="attrs"
              v-on="on"
            >
            </v-text-field>
          </template>
          <v-date-picker
            v-model="dates"
            color="primary"
            no-title
            scrollable
            range
            :max="maxDate"
            :min="minDate"
            @change="onDateChange"
          >
            <v-spacer></v-spacer>
            <v-btn text color="error" @click="menuDates = false">
              {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
            </v-btn>
            <v-btn text color="primary" @click="setDates">
              {{ $vuetify.lang.t("$vuetify.common.ok_btn") }}
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col v-if="last_insights_updated_at !== ''" cols="12" xs="12" md="8">
        <div class="float-end">
          <p class="text-end text-caption ma-0 font-weight-bold">
            {{ this.$vuetify.lang.t("$vuetify.insights.last_updated_at") }}
          </p>
          <p class="text-end text-caption ma-0">
            {{ last_insights_updated_at }}
          </p>
        </div>
      </v-col>
    </v-row>
    <template v-if="$isSectionEnabled('insights','personalizer_stats') && selectedProject.segment.name === 'Personalizer'">
      <Loader v-if="isLoading" />
      <v-row v-else class="d-flex justify-space-between">
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{ this.$vuetify.lang.t("$vuetify.insights.search") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{ this.$vuetify.lang.t("$vuetify.insights.search_txt") }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{ totalNumberOfSearches.toLocaleString() }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageSearch }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageSearch')"
                :color="percentageColor('percentageSearch')"
              >
                {{ percentageIcon("percentageSearch") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{ this.$vuetify.lang.t("$vuetify.insights.recommend") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{ this.$vuetify.lang.t("$vuetify.insights.recommend_txt") }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{ totalNumberOfRecommendations.toLocaleString() }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageRecommend }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageRecommend')"
                :color="percentageColor('percentageRecommend')"
              >
                {{ percentageIcon("percentageRecommend") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{ this.$vuetify.lang.t("$vuetify.insights.orders") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{ this.$vuetify.lang.t("$vuetify.insights.orders_txt") }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{
                kpiSummary &&
                kpiSummary.purchase &&
                kpiSummary.purchase.number_of_orders
                  ? kpiSummary.purchase.number_of_orders.toLocaleString()
                  : "0"
              }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageOrders }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageOrders')"
                :color="percentageColor('percentageOrders')"
              >
                {{ percentageIcon("percentageOrders") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{ this.$vuetify.lang.t("$vuetify.insights.revenue") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{ this.$vuetify.lang.t("$vuetify.insights.revenue_txt") }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{
                kpiSummary &&
                kpiSummary.purchase &&
                kpiSummary.purchase.total_purchased_amounts
                  ? kpiSummary.purchase.total_purchased_amounts.toLocaleString()
                  : "0"
              }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageRevenue }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageRevenue')"
                :color="percentageColor('percentageRevenue')"
              >
                {{ percentageIcon("percentageRevenue") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{ this.$vuetify.lang.t("$vuetify.insights.average_cart_size") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{
                    this.$vuetify.lang.t(
                      "$vuetify.insights.average_cart_size_txt",
                    )
                  }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{ averageCartSize.toLocaleString() }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageAvgCartSize }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageAvgCartSize')"
                :color="percentageColor('percentageAvgCartSize')"
              >
                {{ percentageIcon("percentageAvgCartSize") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{
                this.$vuetify.lang.t("$vuetify.insights.average_order_value")
              }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{
                    this.$vuetify.lang.t(
                      "$vuetify.insights.average_sales_orders_txt",
                    )
                  }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{
                kpiSummary &&
                kpiSummary.purchase &&
                kpiSummary.purchase.avg_purchase_value_by_orders
                  ? kpiSummary.purchase.avg_purchase_value_by_orders.toLocaleString(
                      undefined,
                      { maximumFractionDigits: 2 },
                    )
                  : "0"
              }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageAvgSaleOrder }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageAvgSaleOrder')"
                :color="percentageColor('percentageAvgSaleOrder')"
              >
                {{ percentageIcon("percentageAvgSaleOrder") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{
                this.$vuetify.lang.t("$vuetify.insights.average_sales_users")
              }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{
                    this.$vuetify.lang.t(
                      "$vuetify.insights.average_sales_users_txt",
                    )
                  }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{
                kpiSummary &&
                kpiSummary.purchase &&
                kpiSummary.purchase.avg_purchase_value_by_users
                  ? kpiSummary.purchase.avg_purchase_value_by_users.toLocaleString()
                  : "0"
              }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageAvgSaleUser }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageAvgSaleUser')"
                :color="percentageColor('percentageAvgSaleUser')"
              >
                {{ percentageIcon("percentageAvgSaleUser") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{
                this.$vuetify.lang.t("$vuetify.insights.average_sales_items")
              }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{
                    this.$vuetify.lang.t(
                      "$vuetify.insights.average_sales_items_txt",
                    )
                  }}
                </span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">
              {{
                kpiSummary &&
                kpiSummary.purchase &&
                typeof kpiSummary.purchase.avg_purchase_value_by_items
                  ? kpiSummary.purchase.avg_purchase_value_by_items
                      .toFixed(2)
                      .toLocaleString()
                  : "0"
              }}
            </v-card-text>
            <v-card-text class="d-flex align-center justify-center">
              <span class="mr-2">
                {{ formattedPercentages.percentageAvgSaleItems }}
              </span>
              <v-icon
                v-if="percentageIcon('percentageAvgSaleItems')"
                :color="percentageColor('percentageAvgSaleItems')"
              >
                {{ percentageIcon("percentageAvgSaleItems") }}
              </v-icon>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined flat>
            <v-card-subtitle class="font-weight-bold">
              {{
                this.$vuetify.lang.t("$vuetify.insights.repeated_users_count")
              }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{
                  this.$vuetify.lang.t(
                    "$vuetify.insights.repeated_users_count_txt",
                  )
                }}</span>
              </v-tooltip>
            </v-card-subtitle>
            <v-card-text class="text-center text-h4">{{
              repeatedUsers.length.toLocaleString() || "0"
            }}</v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-row
      v-if="$isSectionEnabled('insights','behavioral_insights') && selectedProject.segment.name === 'Personalizer'"
      align="center"
      justify="center"
    >
      <v-col cols="12">
        <h3 class="text-center">{{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.behavioral_insights") }}</h3>
      </v-col>

      <v-btn-toggle v-model="activeTab" active-class="white">
        <v-btn :disabled="activeTab === 'item'" value="item">
          <span class="tab-card-title">
            {{ this.$vuetify.lang.t("$vuetify.insights.item") }}
          </span>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn :disabled="activeTab === 'user'" value="user">
          <span class="tab-card-title">
            {{ this.$vuetify.lang.t("$vuetify.insights.user") }}
          </span>
        </v-btn>
      </v-btn-toggle>
    </v-row>
    <v-row
      v-if="
        activeTab === 'item' && selectedProject.segment.name === 'Personalizer' && $isSectionEnabled('insights','behavioral_insights')
      "
      class="d-flex justify-space-between"
    >
      <v-col cols="12" md="6">
        <v-card outlined class="mx-auto">
          <v-card-subtitle class="d-flex justify-space-between align-center">
            <div class="font-weight-bold d-flex align-center">
              {{ $vuetify.lang.t("$vuetify.insights.items_counts") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{ $vuetify.lang.t("$vuetify.insights.items_counts_txt") }}
                </span>
              </v-tooltip>
            </div>
            <div class="float-right">
              <v-switch
                class="mt-0"
                dense
                hide-details="true"
                v-model="isLineChart"
                :label="
                  isLineChart
                    ? this.$vuetify.lang.t('$vuetify.insights.line_chart')
                    : 'Bar Chart'
                "
              ></v-switch>
            </div>
          </v-card-subtitle>
          <v-divider></v-divider>
          <Loader v-if="isLoading" class="mx-auto my-6" />
          <div v-else class="d-flex flex-column justify-space-between">
            <div id="chartContainer" style="flex: 1">
              <apexchart
                v-if="isLineChart"
                ref="lineChart"
                type="line"
                :options="itemlineChart"
                :series="[{ data: itemChartData }]"
              ></apexchart>
              <apexchart
                v-else
                ref="barChart"
                width="100%"
                type="bar"
                :options="itemBarChart"
                :series="[{ data: itemChartData }]"
              ></apexchart>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <TopPurchasedItems
          :itemList="
            (kpiSummary &&
              kpiSummary.purchase &&
              kpiSummary.purchase.top_items) ||
            []
          "
          :isLoading="isLoading"
          :start-date="dates[0] || ''"
          :end-date="dates[1] || ''"
        />
      </v-col>
      <v-col cols="12" md="6">
        <TopBrowsedItems
          :itemList="
            (kpiSummary && kpiSummary.browse && kpiSummary.browse.top_items) ||
            []
          "
          :isLoading="isLoading"
          :start-date="dates[0] || ''"
          :end-date="dates[1] || ''"
        />
      </v-col>
      <v-col cols="12" md="6">
        <TopRatedItems
          :itemList="
            (kpiSummary &&
              kpiSummary.ratings &&
              kpiSummary.ratings.top_positive_rated_items) ||
            []
          "
          :isLoading="isLoading"
          :start-date="dates[0] || ''"
          :end-date="dates[1] || ''"
        />
      </v-col>
    </v-row>
    <v-row v-if="activeTab === 'user' && $isSectionEnabled('insights','personalizer_stats')">
      <v-col cols="12" md="6">
        <v-card outlined class="mx-auto">
          <v-card-subtitle class="d-flex justify-space-between align-center">
            <div class="font-weight-bold d-flex align-center">
              {{ $vuetify.lang.t("$vuetify.insights.users_counts") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  {{ $vuetify.lang.t("$vuetify.insights.users_counts_txt") }}
                </span>
              </v-tooltip>
            </div>
            <div class="float-right">
              <v-switch
                class="mt-0"
                dense
                hide-details="true"
                v-model="isLineChart"
                :label="
                  isLineChart
                    ? this.$vuetify.lang.t('$vuetify.insights.line_chart')
                    : 'Bar Chart'
                "
              ></v-switch>
            </div>
          </v-card-subtitle>
          <v-divider></v-divider>
          <Loader v-if="isLoading" class="mx-auto my-6" />
          <div v-else class="d-flex flex-column justify-space-between">
            <div id="chartContainer" style="flex: 1">
              <apexchart
                v-if="isLineChart"
                ref="lineChart"
                type="line"
                :options="userlineChart"
                :series="[{ data: userChartData }]"
              ></apexchart>
              <apexchart
                :key="isLineChart"
                v-else
                ref="barChart"
                width="100%"
                type="bar"
                :options="userBarChart"
                :series="[{ data: userChartData }]"
              ></apexchart>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card rounded outlined class="mx-auto" height="100%">
          <v-card-subtitle
            class="font-weight-bold d-flex justify-space-between align-center"
          >
            <div class="d-flex align-center">
              {{ this.$vuetify.lang.t("$vuetify.insights.top_users") }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs" class="ml-1">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                {{ this.$vuetify.lang.t("$vuetify.insights.top_users_txt") }}
              </v-tooltip>
            </div>
            <v-btn
              small
              color="primary"
              @click="downloadAsCSV('top_users', userHeaders)"
              :loading="downloading"
              :disabled="downloading || topUsers.length === 0 || !$hasProjectAccessFor([$pRole.ADMIN])"
            >
              <v-icon left small>mdi-download</v-icon> CSV
            </v-btn>
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-data-table
            :headers="userHeaders"
            :items="topUsers"
            :items-per-page="itemsPerPage"
            :sort-by="['total_purchased_amount']"
            :sort-desc="[true]"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td>{{ item.user_id }}</td>
                <td>{{ item.member_id || "N/A" }}</td>
                <td>{{ item.total_purchased_amount.toLocaleString() }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <RepeatedUsers
          :repeatedUsers="repeatedUsers"
          :isLoading="isLoading"
          :start-date="dates[0] || ''"
          :end-date="dates[1] || ''"
        />
      </v-col>
    </v-row>

    <!--  Conversation history  -->
    <v-row>
      <v-col
          v-if="
          $isSectionEnabled('insights','conversation_history') &&
           (selectedProject.segment.name === PROJECT_SEGMENTS.Maira ||
          selectedProject.segment.name === PROJECT_SEGMENTS.PERSONALIZER)
        "
        xs="12"
        md="12"
        class="pa-0 px-3"
      >
        <v-card outlined class="mx-auto">
          <template>
            <!-- integrate after get insights data  -->
            <v-card-subtitle
              class="font-weight-bold"
            >
              <div class="d-flex flex-column flex-sm-row justify-space-between align-sm-center">
                <div class="d-flex align-center mb-2 mb-sm-0">
                  {{ $vuetify.lang.t("$vuetify.gpt_history.gpt_conversation_history_title") }}
                  &nbsp;
                  <v-tooltip top>
                    <template #activator="{ on: tooltipOn, attrs }">
                      <v-icon small v-on="tooltipOn" v-bind="attrs">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      {{
                        $vuetify.lang.t(
                          "$vuetify.gpt_history.gpt_conversation_history_title",
                        )
                      }}
                    </span>
                  </v-tooltip>
                </div>
                <div class="d-flex flex-row align-center">
                  <v-btn
                    class="mr-1"
                    small
                    color="primary"
                    @click="gptConversationFileGenerationModal = true"
                    :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    <span class="hidden-sm-and-down"> CSV </span>
                  </v-btn>
                  <v-btn
                    class="ml-1"
                    small
                    color="primary"
                    @click="gptConversationHistoryModal = true"
                    :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                  >
                    <v-icon left small>mdi-history</v-icon>
                    <span class="hidden-sm-and-down">
                      {{ $vuetify.lang.t("$vuetify.common.history") }}
                    </span>
                  </v-btn>
                </div>
              </div>
            </v-card-subtitle>
            <v-divider></v-divider>

            <v-row class="d-flex flex-column flex-md-row py-4 mb-n6 px-6">
              <v-col width class="pa-0 pr-0 pr-md-3">
                <v-text-field
                  v-model="conversationSearch"
                  :label="$vuetify.lang.t('$vuetify.gpt_history.search_txt')"
                  outlined
                  dense
                  clearable
                ></v-text-field>
              </v-col>
              <!-- <v-col width class="pa-0 pr-0 pr-md-3">
                <v-text-field
                  v-model="userFilter"
                  :label="
                    $vuetify.lang.t('$vuetify.gpt_history.gpt_history_search_user_id')
                  "
                  outlined
                  dense
                  clearable
                ></v-text-field>
              </v-col> -->
              <!-- <v-col xs="12" md="3">
              <v-text-field
                v-model="conversationFilter"
                label="Search Conversation ID"
                outlined
                dense
                clearable
              ></v-text-field>
            </v-col> -->
              <!-- <v-col class="pa-0 pr-0 pr-md-3">
                <v-text-field
                  v-model="promptFilter"
                  :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_search_query')"
                  outlined
                  dense
                  clearable
                ></v-text-field>
              </v-col> -->
              <v-col class="pa-0 pr-0 pr-md-3">
                <v-select
                  v-model="feedbackFilter"
                  :items="feedbackOptions"
                  :label="
                    $vuetify.lang.t('$vuetify.gpt_history.gpt_history_filter_feedback')
                  "
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col class="pa-0 pr-0 pr-md-3">
                <v-select
                  v-model="statusFilter"
                  :items="statusOptions"
                  :label="
                    $vuetify.lang.t('$vuetify.gpt_history.status')
                  "
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col class="pa-0 pr-0 pr-md-3">
                <v-select
                  v-model="archiveFilter"
                  :items="archiveOptions"
                  :label="
                    $vuetify.lang.t('$vuetify.gpt_history.filter_archive')
                  "
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col class="pa-0 pr-0">
                <v-select
                  v-model="endpointFilter"
                  :items="endpointOptions"
                  :label="
                    $vuetify.lang.t('$vuetify.gpt_history.gpt_history_filter_endpoints')
                  "
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col v-if="is_admin" class="pa-0 pr-0 pr-md-3 ml-md-3">
                <v-autocomplete
                  v-model="selectedProfileId"
                  :items="gptProfiles"
                  item-text="name"
                  item-value="id"
                  :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_profile_name')"
                  outlined
                  dense
                  clearable
                  :loading="isLoading"
                  @change="handleProfileChange"
                >
                  <template
                    v-slot:append-item
                    v-if="hasMoreProfiles"
                  >
                    <v-list-item>
                      <v-list-item-content>
                        <div class="d-flex">
                          <v-btn
                            class="ma-0 pa-0"
                            @click="getGPTProfiles()"
                            color="primary"
                            text
                          >
                            {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                          </v-btn>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-divider></v-divider>
            <v-data-table
              :headers="gptHeaders"
              :items="gptConversations"
              show-expand
              :multi-sort="true"
              :options.sync="gptConversationHistoryOptions"
              :server-items-length="gptTotalConversations"
              :loading="conversationTableLoading"
              :loading-text="$vuetify.lang.t('$vuetify.common.loading_text')"
            >
              <template v-slot:[`item.requestBody.userId`]="{ item }">
                <span>
                  {{
                    item.requestBody.userId
                      ? item.requestBody.userId.split("-")[0]
                      : ""
                  }}
                </span>
              </template>
              <template v-slot:[`item.requestBody.userName`]="{ item }">
                {{ item.requestBody.userName || "N/A"}}
              </template>
              <template v-slot:[`item.requestBody.memberId`]="{ item }">
                <span>
                  {{
                    item.requestBody.memberId
                      ? (item.requestBody.memberId.includes("-")
                          ? item.requestBody.memberId.split("-")[0]
                          : item.requestBody.memberId
                        ).slice(0, 8)
                      : ""
                  }}
                </span>
              </template>
              <template v-slot:[`item.requestBody.query`]="{ item }">
                <span>
                  {{
                    String(item.requestBody?.query || "")
                      .slice(0, 50) + (item.requestBody?.query?.length > 50 ? "..." : "")
                  }}
                </span>
              </template>
              <template v-slot:[`item.requestBody.name`]="{ item }">
                <span>
                  {{
                    item.requestBody.name > 30
                      ? item.requestBody.name.slice(0, 30) + "..."
                      : item.requestBody.name || "N/A"
                  }}
                </span>
              </template>
              <template v-slot:[`item.messageSeen`]="{ item }">
                <span>
                  <v-chip
                    small
                    dark
                    :color="item.messageSeen === false ? 'grey' : 'primary'"
                  >
                    {{ item.messageSeen === false ? "Unseen" : "Seen" }}
                  </v-chip>
                </span>
              </template>
              <template v-slot:[`item.feedback`]="{ item }">
                <span>
                  <template v-if="item.feedback && item.feedback.type">
                    {{ item.feedback.type }}
                  </template>
                  <template v-else> N/A </template>
                </span>
              </template>
              <template v-slot:[`item.tokens`]="{ item }">
                <span>{{ item.tokens }}</span>
              </template>
              <template v-slot:[`item.requestUrl`]="{ item }">
                <span>
                  {{ determineEndpointType(item.requestUrl) }}
                </span>
              </template>
              <template v-slot:[`item.ipAddress`]="{ item }">
                <span>
                  {{ item.ipAddress || "N/A" }}
                </span>
              </template>
              <template v-slot:[`item.createdAt`]="{ item }">
                <div>
                  {{ formatDateTime(item.createdAt) }}
                </div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex align-center">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        class="mr-2"
                        @click="redirectConversationDetails(item)"
                      >mdi-eye</v-icon>
                    </template>
                    <span>{{ $vuetify.lang.t("$vuetify.common.details_header_txt") }}</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        class="ml-1"
                        :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                        @click="openTrainingDataModal(item)"
                      >mdi-plus-circle</v-icon>
                    </template>
                    <span>
                      {{ $vuetify.lang.t("$vuetify.gpt_history.add_to_training_data") }}
                    </span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.linkedDatasets`]="{ item }">


              <template v-if="getLinkedDatasetNames(item).length">

                    <v-chip
                      v-for="name in getLinkedDatasetNames(item).slice(0, 2)"
                      :key="name"
                      class="ma-1"
                      color="primary"
                      small
                      dark
                    >
                      {{ name }}
                    </v-chip>

                    <span v-if="getLinkedDatasetNames(item).length > 2">
                      +{{ getLinkedDatasetNames(item).length - 2 }} {{ $vuetify.lang.t("$vuetify.common.more") }}
                    </span>
              </template>


              <template v-else>
                N/A
              </template>

              </template>
              <template v-slot:expanded-item="{  item }">
                  <td :colspan="gptHeaders.length + 2">
                  <v-row>
                    <v-col cols="12">
                        <div class="text-subtitle-2 font-weight-medium mb-1">
                          {{$vuetify.lang.t('$vuetify.gpt_history.show_linked_datasets')}}
                        </div>
                        <v-chip-group column>
                          <v-chip
                            v-for="(name, index) in getLinkedDatasetNames(item)"
                            :key="index"
                            class="ma-1"
                            color="primary"
                            small
                            dark
                          >
                            {{ name }}
                          </v-chip>
                        </v-chip-group>
                    </v-col>
                  </v-row>
                  </td>
              </template>
            </v-data-table>
          </template>
        </v-card>
      </v-col>
    </v-row>
        <!-- Maira Sessions -->
     <v-row>
      <v-col
        v-if="
        $isSectionEnabled('insights','maira_sessions') &&
          (selectedProject.segment.name === PROJECT_SEGMENTS.Maira ||
            selectedProject.segment.name === PROJECT_SEGMENTS.PERSONALIZER)
        "
        xs="12"
        md="12"
        class="pa-0 px-3"
      >
        <MairaSessions
          :start-date="start_date"
          :end-date="end_date"
          @session-status-updated="onSessionStatusUpdated"
        />
      </v-col>
    </v-row>
    <!-- Questionnaire Responses -->
    <v-row>
      <v-col
        v-if="
          (selectedProject.segment.name === PROJECT_SEGMENTS.Maira ||
            selectedProject.segment.name === PROJECT_SEGMENTS.PERSONALIZER) &&
          $hasProjectAccessFor([$pRole.ADMIN])
        "
        class="pa-0 px-3"
        xs="12"
        md="12"
      >
        <QuestionnaireResponses
          ref="questionnaireResponses"
          :start-date="start_date"
          :end-date="end_date"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-if="
          $isSectionEnabled('insights', 'conversation_insights') &&
          (selectedProject.segment.name === PROJECT_SEGMENTS.Maira ||
            selectedProject.segment.name === PROJECT_SEGMENTS.PERSONALIZER)
        "
        xs="12"
        md="12"
        class="pa-0 px-3"
      >
        <ConversationInsight
          :projectId="selectedProject.id"
          :startDate="start_date || (dates && dates[0])"
          :endDate="end_date || (dates && dates[1])"
          :gptProfiles="gptProfiles"
        />
      </v-col>
    </v-row>

    <!--  Questionnaire log  -->
    <v-row>
      <v-col
        v-if="$isSectionEnabled('insights','questionnaire_logs') && (selectedProject.segment.name === PROJECT_SEGMENTS.PERSONALIZER)"
        xs="12"
        md="12"
        class="pa-0 px-3"
      >
        <QuestionnaireLog :startDate="start_date" :endDate="end_date" />
      </v-col>
    </v-row>

    <!-- API call response summary -->
    <v-row v-if="$isSectionEnabled('insights','api_call_response_summary') && selectedProject.segment.name === 'Personalizer'">
      <v-col xs="12" md="12">
        <v-card rounded outlined class="mx-auto px-2">
          <v-col md="6">
            <div class="primary--text font-weight-bold ml-n1">
              {{ $vuetify.lang.t("$vuetify.gpt_history.api_call_response_summary") }}
            </div>
          </v-col>
          <v-divider></v-divider>
          <Loader v-if="isLoading" class="mr-2 mt-6" />
          <v-data-table
            v-else
            :headers="headers"
            :items="requestDetails"
            :items-per-page="itemsPerPage"
          >
            <template v-slot:[`item.request_path`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">{{ item.request_path }}</span>
                </template>
                <span>{{ item.request_path }}</span>
              </v-tooltip>
            </template>

            <template v-slot:[`item.number_of_request`]="{ item }">
              <div>
                {{ item.number_of_request }}
              </div>
            </template>

            <template v-slot:[`item.avg_processing_time`]="{ item }">
              <div>
                {{ item.avg_processing_time.toFixed(5) }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <!-- Device, Browser & OS ratio -->
    <template v-if="$isSectionEnabled('insights','api_call_response_summary') && selectedProject.segment.name === 'Personalizer'">
      <v-container fluid>
        <v-row justify="center">
          <v-col cols="12" sm="6" md="4">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.insights.device_ratio_txt") }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-on="on" v-bind="attrs">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>
                    {{ $vuetify.lang.t("$vuetify.insights.show_device_ratio_txt") }}
                  </span>
                </v-tooltip>
              </v-card-subtitle>
              <v-card-text>
                <Loader v-if="isLoading" />
                <v-container
                  v-else-if="
                    deviceRatio &&
                    Object.keys(deviceRatio).length > 0 &&
                    Object.values(deviceRatio).some(value => value !== 0)
                  "
                >
                  <v-row>
                    <v-col cols="12">
                      <div id="chart">
                        <apexchart
                          type="pie"
                          width="100%"
                          :options="deviceRatioOptions"
                          :series="deviceRatioValue"
                        >
                        </apexchart>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
                <v-card v-else flat height="250">
                  <v-row class="fill-height" align="center" justify="center">
                    <span class="grey--text">
                      {{ $vuetify.lang.t("$vuetify.common.no_data_available") }}
                    </span>
                  </v-row>
                </v-card>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.insights.browser_ratio_txt") }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-on="on" v-bind="attrs">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>
                    {{ $vuetify.lang.t("$vuetify.insights.show_browser_ratio_txt") }}
                  </span>
                </v-tooltip>
              </v-card-subtitle>
              <v-card-text>
                <Loader v-if="isLoading" />
                <v-container
                  v-else-if="
                    browserRatio &&
                    Object.keys(browserRatio).length > 0 &&
                    Object.values(browserRatio).some(value => value !== 0)
                  "
                >
                  <v-row>
                    <v-col cols="12">
                      <div id="chart">
                        <apexchart
                          type="pie"
                          width="100%"
                          :options="browserRatioOptions"
                          :series="browserRatioValue"
                        >
                        </apexchart>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
                <v-card v-else flat height="250">
                  <v-row class="fill-height" align="center" justify="center">
                    <span class="grey--text">
                      {{ $vuetify.lang.t("$vuetify.common.no_data_available") }}
                    </span>
                  </v-row>
                </v-card>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.insights.os_ratio_txt") }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-on="on" v-bind="attrs">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>
                    {{ $vuetify.lang.t("$vuetify.insights.show_os_ratio_txt") }}
                  </span>
                </v-tooltip>
              </v-card-subtitle>
              <v-card-text>
                <Loader v-if="isLoading" />
                <v-container
                  v-else-if="
                    osRatio &&
                    Object.keys(osRatio).length > 0 &&
                    Object.values(osRatio).some(value => value !== 0)
                  "
                >
                  <v-row>
                    <v-col cols="12">
                      <div id="Chart">
                        <apexchart
                          type="pie"
                          width="102%"
                          :options="osRatioOptions"
                          :series="osRatioValue"
                        ></apexchart>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
                <v-card v-else flat height="250">
                  <v-row class="fill-height" align="center" justify="center">
                    <span class="grey--text">
                      {{ $vuetify.lang.t("$vuetify.common.no_data_available") }}
                    </span>
                  </v-row>
                </v-card>
              </v-card-text>
            </v-card>
          </v-col>
              </v-row>
              </v-container>
              </template>
    <!-- Add Gpt Response to Training Data Modal -->
    <template>
      <v-dialog persistent v-model="openTrainModal" max-width="60%">
        <v-card style="overflow-y: scroll; overflow-x: hidden">
          <div class="d-flex flex-column modalWrapper">
            <p class="training-header">
              {{
                $vuetify.lang.t("$vuetify.gpt_history.add_gpt_response_to_training_data")
              }}
            </p>
            <label class="text-subtitle-1 text-medium-emphasis">
              {{ $vuetify.lang.t("$vuetify.gpt_history.gpt_history_conversation_id") }}:
              <span class="conversation_id">
                {{ trainingData.conversation_id }}
              </span>
            </label>

            <v-form ref="documentForm">
              <div class="inputWrapper">
                <v-select
                  @change="handleDataset"
                  v-model="selectedDataset"
                  :items="datasets"
                  item-text="name"
                  :label="$vuetify.lang.t('$vuetify.gpt_dataset.select_dataset')"
                  outlined
                  item-value="dataset_id"
                  :rules="getRules($vuetify.lang.t('$vuetify.gpt_dataset.select_dataset'))"
                >
                </v-select>
                <div v-if="!newDataSet" class="dummy-width"></div>
              </div>
            </v-form>
            <template v-if="newDataSet">
              <div>
                <v-form ref="datasetForm">
                  <v-row>
                    <v-col cols="12" md="12">
                      <v-text-field
                        type="text"
                        v-model="newDatasetName"
                        :label="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_name')"
                        :rules="[
                          required($vuetify.lang.t('$vuetify.gpt_dataset.dataset_name')),
                        ]"
                        outlined
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        type="text"
                        v-model="indexColumnName"
                        :label="$vuetify.lang.t('$vuetify.gpt_dataset.index_column_name')"
                        :rules="[
                          required(
                            $vuetify.lang.t('$vuetify.gpt_dataset.index_column_name'),
                          ),
                        ]"
                        outlined
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        type="text"
                        v-model="secondaryIndex"
                        :label="
                          $vuetify.lang.t('$vuetify.gpt_dataset.secondary_index_column')
                        "
                        outlined
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        type="text"
                        v-model="imageColumn"
                        :label="$vuetify.lang.t('$vuetify.gpt_dataset.image_url_column')"
                        outlined
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        type="text"
                        v-model="description"
                        :label="
                          $vuetify.lang.t('$vuetify.gpt_dataset.description_label_txt')
                        "
                        outlined
                      />
                    </v-col>
                    <v-checkbox
                      style="margin-left: 10px"
                      v-model="fillUpEmptyIndex"
                      :label="
                        $vuetify.lang.t('$vuetify.gpt_dataset.fill_up_index_if_empty')
                      "
                    ></v-checkbox>
                    <v-col cols="12" md="12">
                      <v-file-input
                        outlined
                        accept=".json, .csv, application/json, text/csv"
                        @change="handleFileChange"
                        :label="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_file')"
                      ></v-file-input>
                    </v-col>
                  </v-row>
                </v-form>
                <div class="btn-wrapper" style="margin-top: 20px">
                  <v-btn
                    type="reset"
                    class="red--text font-weight-bold"
                    text
                    outlined
                    @click="closeTrainModal"
                    style="margin-right: 14px"
                  >
                    {{ this.$vuetify.lang.t("$vuetify.common.cancel_btn") }}
                  </v-btn>
                  <v-btn
                    @click="createDataset()"
                    color="primary"
                    style=""
                    class="add-btn"
                    >{{ $vuetify.lang.t("$vuetify.gpt_dataset.create_dataset") }}</v-btn
                  >
                </div>
              </div>
            </template>
            <template v-else>
              <div class="inputWrapper">
                <v-textarea
                  type="text"
                  auto-grow
                  v-model="trainingData.query"
                  :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_query')"
                  outlined
                />
                <input
                  class="checkbox"
                  type="checkbox"
                  name="myCheckbox"
                  v-model="selectedResponseFields.query"
                />
              </div>
              <div class="inputWrapper">
                <v-textarea
                  type="text"
                  auto-grow
                  v-model="trainingData.response"
                  :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_response')"
                  outlined
                />
                <input
                  class="checkbox"
                  type="checkbox"
                  name="myCheckbox"
                  v-model="selectedResponseFields.response"
                />
              </div>
              <div class="inputWrapper">
                <v-textarea
                  type="text"
                  auto-grow
                  v-model="trainingData.feedback"
                  :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_feedback')"
                  outlined
                />
                <input
                  class="checkbox"
                  type="checkbox"
                  name="myCheckbox"
                  v-model="selectedResponseFields.feedback"
                />
              </div>
              <v-form ref="conversationMetadata">
                <div class="inputWrapper">
                  <v-textarea
                    type="text"
                    auto-grow
                    v-model="trainingData.conversation_metadata"
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.gpt_history.gpt_history_conversation_metadata',
                      )
                    "
                    outlined
                    :rules="
                      selectedResponseFields.conversation_metadata
                        ? [validateConversationMetadata]
                        : []
                    "
                  />
                  <input
                    class="checkbox"
                    type="checkbox"
                    name="myCheckbox"
                    v-model="selectedResponseFields.conversation_metadata"
                  />
                </div>
              </v-form>
              <div class="btn-wrapper">
                <v-btn
                  type="reset"
                  class="red--text font-weight-bold"
                  text
                  outlined
                  @click="closeTrainModal"
                  style="margin-right: 14px"
                >
                  {{ this.$vuetify.lang.t("$vuetify.common.cancel_btn") }}
                </v-btn>
                <v-btn
                  @click="addTrainingData"
                  color="primary"
                  style="margin-right: 44px; min-width: 120px"
                  class="add-btn"
                  >{{ $vuetify.lang.t("$vuetify.common.add_btn") }}</v-btn
                >
              </div>
            </template>
          </div>
        </v-card>
      </v-dialog>
    </template>
    <!--Conversation History File Generation and History Modal -->
    <template v-if="gptConversationFileGenerationModal">
      <FileGenerationModal
        :open="gptConversationFileGenerationModal"
        :closeModal="closeGptConversationFileGenerationModal"
        :title="
          $vuetify.lang.t(
            '$vuetify.logs_file_generation.conversation_logs_file_generation_title',
          )
        "
        :downloadRequestTxt="
          $vuetify.lang.t('$vuetify.logs_file_generation.conversation_history_download_request_txt')
        "
        :fileGenerationInstructionTxt="
          $vuetify.lang.t('$vuetify.logs_file_generation.generate_conversation_history_instruction')
        "
        :requestFileGeneration="conversationDownloadRequest"
        :prefix="conversation_history_prefix"
        :history_type="HISTORY_TYPES.CONVERSATION"
        :filters="{
          start_date,
          end_date,
          feedbackFilter,
          endpointFilter,
          statusFilter,
          archiveFilter,
          selectedProfileId,
          search_text: conversationSearch,
        }"
      />
    </template>
    <template v-if="gptConversationHistoryModal">
      <FileGeneratedHistoryModal
        :open="gptConversationHistoryModal"
        :closeModal="closeGptConversationHistoryModal"
        :title="
          $vuetify.lang.t(
            '$vuetify.logs_file_generation.conversation_logs_file_generated_history_title',
          )
        "
        :prefix="conversation_history_prefix"
        :history_type="HISTORY_TYPES.CONVERSATION"
      />
    </template>
  </v-container>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import {
  ADD_CONVERSATION_TO_DATASET,
  CREATE_DATASET,
  GET_GPT_DATASETS,
  RECOMMENDER_INSIGHTS,
  RECOMMENDER_UPDATE_FEEDBACK,
  RETRIEVE_GPT_CONVERSATIONS,
  UPLOAD_DATASET,
  GENERATE_CONVERSATION_HISTORY,
  GET_GPT_PROFILES
} from "@/store/_actiontypes";
import { SEGMENTS, HISTORY_TYPES, ENDPOINT_TYPES, ENDPOINT_PATHS } from "@/global/_constants";
import { DATETIME_PICKER, TEXT } from "@/components/common/input/_inputTypes";
import { dateOfToday, encryptData, authorize, createCSVBlob } from "@/helpers/helper";
import { Role } from "@/helpers/role";
import Loader from "@/components/common/loader/Loader";
import VueApexCharts from "vue-apexcharts";
import TopBrowsedItems from "./Insights/TopBrowsedItems.vue";
import TopPurchasedItems from "./Insights/TopPurchasedItems.vue";
import TopRatedItems from "./Insights/TopRatedItems.vue";
import RepeatedUsers from "./Insights/RepeatedUsers.vue";
import QuestionnaireLog from "./Insights/QuestionnaireLog.vue";
import MairaSessions from "./Insights/MairaSessions.vue";
import QuestionnaireResponses from "./Insights/QuestionnaireResponses.vue";
import ConversationInsight from "./Insights/ConversationInsight.vue";
import { debounce } from "lodash";
import FileGenerationModal from "@/components/common/FileGenerationModal";
import FileGeneratedHistoryModal from "@/components/common/FileGeneratedHistoryModal";


export default {
  components: {
    Loader,
    apexchart: VueApexCharts,
    TopBrowsedItems,
    TopPurchasedItems,
    TopRatedItems,
    RepeatedUsers,
    QuestionnaireLog,
    FileGenerationModal,
    FileGeneratedHistoryModal,
    MairaSessions,
    QuestionnaireResponses,
    ConversationInsight
  },
  data() {
    return {
      isLoading: false,
      conversationTableLoading: false,
      minDate: null,
      PROJECT_SEGMENTS: SEGMENTS,
      HISTORY_TYPES: HISTORY_TYPES,
      gptConversationFileGenerationModal: false,
      gptConversationHistoryModal: false,
      dateOfToday,
      TEXT: TEXT,
      DATETIME_PICKER: DATETIME_PICKER,
      start_date: "",
      end_date: "",
      last_insights_updated_at: "",
      menuDates: false,
      dynamicRowCount: 1,
      gptConversationHistoryOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      snackbarMessage: "",
      dates: [],
      on: {},
      activeTab: "item",
      tab: null,
      userHeaders: [
        { text: "User ID", value: "user_id", sortable: false },
        { text: "Member ID", value: "member_id", sortable: false },
        { text: "Purchased Amount", value: "purchased_amount", sortable: true },
      ],
      selectedDataset: "",
      dataNotSelectedError: false,
      datasets: [
        {
          name: this.$vuetify.lang.t("$vuetify.gpt_dataset.add_new_dataset"),
          dataset_id: "new_dataset",
        },
      ],
      selectedResponseFields: {
        query: true,
        response: true,
        feedback: true,
        conversation_metadata: false,
      },
      headers: [
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.request_path_header_txt"),
          align: "start",
          sortable: true,
          value: "request_path",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.number_of_request_header_txt"),
          align: "start",
          sortable: true,
          value: "number_of_request",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.avg_processing_time_header_txt"),
          align: "start",
          sortable: true,
          value: "avg_processing_time",
        },
      ],
      gptHeaders: [
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_user_id"),
          align: "start",
          sortable: false,
          value: "requestBody.userId",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_user_name"),
          align: "start",
          sortable: false,
          value: "requestBody.userName",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_member_id"),
          align: "start",
          sortable: false,
          value: "requestBody.memberId",
        },
        // {
        //   text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_conversation_id"),
        //   align: "start",
        //   sortable: true,
        //   value: "conversation_id",
        // },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_query"),
          align: "start",
          sortable: false,
          value: "requestBody.query",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.status"),
          align: "start",
          sortable: false,
          value: "messageSeen",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_profile_name"),
          align: "start",
          sortable: false,
          value: "requestBody.name",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_feedback"),
          align: "start",
          sortable: false,
          value: "feedback",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_consumed_tokens"),
          align: "start",
          sortable: true,
          value: "tokens",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_endpoint"),
          align: "start",
          sortable: false,
          value: "requestUrl",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.common.ip_address"),
          align: "start",
          sortable: false,
          value: "ipAddress",
        },
        // {
        //   text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_response"),
        //   align: "start",
        //   sortable: true,
        //   value: "response_msg",
        // },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_date"),
          align: "start",
          sortable: true,
          value: "createdAt",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.show_linked_datasets"),
          align: "start",
          sortable: false,
          value: "linkedDatasets",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_history.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ],
      optionalDataMapper: {
        secondary_idx_column: "secondaryIndex",
        image_url_column: "imageColumn",
        description: "description",
        dataset_file: "datasetFile",
      },
      conversation_history_prefix: "conversation_history_download",
      required(fieldName) {
      return (value) => {
        return !!value || `${fieldName}${this.$vuetify.lang.t('$vuetify.validation.required_error_msg_txt')}`;
      };
    },
      getRules(fieldName) {
        const rules = [];
        if (!this.newDataSet) {
          rules.push(value => !!value || `${fieldName} is required`);
        }
        return rules;
      },
      snackbar: false,
      timeout: 6000,
      snackbarColor: "success",
      newDataSet: false,
      newDatasetName: "",
      indexColumnName: "",
      secondaryIndex: "",
      fillUpEmptyIndex: true,
      imageColumn: "",
      description: "",
      datasetFile: null,
      itemsPerPage: 5,
      openTrainModal: false,
      trainingData: {},
      userFilter: "",
      conversationSearch: "",
      conversationFilter: "",
      promptFilter: "",
      feedbackFilter: "",
      endpointFilter: "",
      statusFilter: null,  // since statusFilter is boolean, not string
      feedbackOptions: ["positive", "negative"],
      statusOptions: [
        { text: "Seen", value: true },
        { text: "Unseen", value: false }
      ],
      archiveFilter: null,
      archiveOptions: [
        { text: this.$vuetify.lang.t("$vuetify.gpt_history.active"), value: false },
        { text: this.$vuetify.lang.t("$vuetify.gpt_history.archived"), value: true }
      ],
      percentageChange: 0,
      percentageRevenue: 0,
      percentageOrders: 0,
      percentageAvgSaleUser: 0,
      percentageAvgSaleOrder: 0,
      percentageAvgSaleItems: 0,
      percentageAvgCartSize: 0,
      percentageRecommend: 0,
      percentageSearch: 0,
      totalNumberOfSearches: 0,
      totalNumberOfRecommendations: 0,
      averageCartSize: 0,
      oldValue: 0,
      newValue: 0,
      isLineChart: false,
      endpointOptions: [ENDPOINT_TYPES.ASK, ENDPOINT_TYPES.VISION, ENDPOINT_TYPES.REALTIME, ENDPOINT_TYPES.QUESTIONNAIRE],
      downloading: false,
      deviceRatioValue: [],
      browserRatioValue: [],
      osRatioValue: [],
      gptConversations: [],
      selectedProfileId: null,
      gptProfiles: [],
      currentPage: 0,
      pageSize: 100,
      hasMoreProfiles: false,
      loadingProfiles: false,
      is_admin: authorize([Role.ADMIN]),
      deviceRatioOptions: {
        chart: {
          width: 380,
          type: "pie",
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: function (val) {
              return (val * 100).toFixed(2) + "%";
            },
          },
        },
        labels: [],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
              cursor: {
                show: true,
              },
            },
          },
        ],
      },
      browserRatioOptions: {
        chart: {
          width: 380,
          type: "pie",
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: function (val) {
              return (val * 100).toFixed(2) + "%";
            },
          },
        },
        labels: [],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      osRatioOptions: {
        chart: {
          width: 380,
          type: "pie",
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: function (val) {
              return (val * 100).toFixed(2) + "%";
            },
          },
        },
        labels: [],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      itemlineChart: {
        chart: {
          height: 400,
          type: "line",
          toolbar: {
            show: false,
            tools: {
              download: false,
            },
            autoSelected: "zoom",
          },
        },
        plotOptions: {
          line: {
            curve: "smooth",
            markers: {
              size: 6,
              colors: ["#008FFB"],
              strokeColors: "#fff",
              strokeWidth: 2,
              hover: {
                size: 8,
              },
            },
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: [
            "Browsed",
            "Positive Rated",
            "Negative Rated",
            "Purchased",
          ],
        },
        yaxis: {
          logarithmic: true,
          min: 1,
        },
        tooltip: {
          x: {
            formatter: function () {
              return "";
            },
          },
          y: {
            formatter: function (val, { dataPointIndex, _w }) {
              const categories = [
                "Browse",
                "Positive Rated",
                "Negative Rated",
                "Purchase",
              ];
              const label = categories[dataPointIndex];
              return `${label}: ${val.toLocaleString()}`;
            },
            title: {
              formatter: function () {
                return "";
              },
            },
          },
          followCursor: true,
        },
        title: {
          text: this.$vuetify.lang.t("$vuetify.insights.items_counts"),
          align: "center",
          margin: 20,
          style: {
            fontSize: "20px",
            fontFamily: "Helvetica, Arial, sans-serif",
            color: "#333333",
          },
        },
      },
      userlineChart: {
        chart: {
          height: 400,
          type: "line",
          toolbar: {
            show: false,
            tools: {
              download: false,
            },
            autoSelected: "zoom",
          },
        },

        plotOptions: {
          line: {
            curve: "smooth",
            markers: {
              size: 6,
              colors: ["#008FFB"],
              strokeColors: "#fff",
              strokeWidth: 2,
              hover: {
                size: 8,
              },
            },
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: ["Browsed", "Rated", "Purchased"],
        },
        yaxis: {
          logarithmic: true,
          min: 2,
        },
        tooltip: {
          x: {
            formatter: function () {
              return "";
            },
          },
          y: {
            formatter: function (val, { dataPointIndex, _w }) {
              const categories = ["Browse", "Rated", "Purchase"];
              const label = categories[dataPointIndex];
              return `${label}: ${val.toLocaleString()}`;
            },
            title: {
              formatter: function () {
                return "";
              },
            },
          },
          followCursor: true,
        },
        title: {
          text: this.$vuetify.lang.t("$vuetify.insights.users_counts"),
          align: "center",
          margin: 20,
          style: {
            fontSize: "20px",
            fontFamily: "Helvetica, Arial, sans-serif",
            color: "#333333",
          },
        },
      },
      itemBarChart: {
        chart: {
          type: "bar",
          height: 400,
          toolbar: {
            show: false,
            tools: {
              download: false,
            },
            autoSelected: "zoom",
          },
        },
        colors: ["#008FFB", "#FF4560", "#33B2DF", "#00E396"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
            dataLabels: {
              position: "bottom",
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: [
            "Browse",
            "Positive Rated",
            "Negative Rated",
            "Purchase",
          ],
          labels: {
            style: {
              colors: "#000000",
              fontSize: "14px",
              fontFamily: "Helvetica, Arial, sans-serif",
            },
          },
        },
        yaxis: {
          logarithmic: true,
          min: 1,
          // tickAmount: 'dataPoints',
          // forceNiceScale: false
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          x: {
            formatter: function () {
              return "";
            },
          },
          y: {
            formatter: function (val, { dataPointIndex, _w }) {
              const categories = [
                "Browse",
                "Positive Rated",
                "Negative Rated",
                "Purchase",
              ];
              const label = categories[dataPointIndex];
              return `${label}: ${val.toLocaleString()}`;
            },
            title: {
              formatter: function () {
                return "";
              },
            },
          },
          followCursor: true,
        },
        title: {
          text: this.$vuetify.lang.t("$vuetify.insights.items_counts"),
          align: "center",
          margin: 20,
          style: {
            fontSize: "20px",
            fontFamily: "Helvetica, Arial, sans-serif",
            color: "#333333",
          },
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 300,
              },
              xaxis: {
                labels: {
                  style: {
                    fontSize: "12px",
                  },
                },
              },
              yaxis: {
                logarithmic: true,
                labels: {
                  style: {
                    fontSize: "12px",
                  },
                },
              },
              title: {
                style: {
                  fontSize: "16px",
                },
              },
            },
          },
        ],
      },
      userBarChart: {
        chart: {
          type: "bar",
          height: 400,
          toolbar: {
            show: false,
            tools: {
              download: false,
              // selection: true,
              // zoom: true,
              // zoomin: true,
              // zoomout: true,
              // pan: true,
              // reset: true | '<img src="/static/icons/reset.png" width="20">',
              // customIcons: [],
            },
            autoSelected: "zoom",
          },
        },
        colors: ["#008FFB", "#FF4560", "#33B2DF", "#00E396"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
            dataLabels: {
              position: "bottom",
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: ["Browsed", "Rated", "Purchase"],
          labels: {
            style: {
              colors: "#000000",
              fontSize: "14px",
              fontFamily: "Helvetica, Arial, sans-serif",
            },
          },
        },
        yaxis: {
          logarithmic: true,
          min: 2,
          labels: {
            style: {
              fontSize: "14px",
              fontFamily: "Helvetica, Arial, sans-serif",
            },
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          x: {
            formatter: function () {
              return "";
            },
          },
          y: {
            formatter: function (val, { dataPointIndex, _w }) {
              const categories = ["Browse", "Rated", "Purchase"];
              const label = categories[dataPointIndex];
              return `${label}: ${val.toLocaleString()}`;
            },
            title: {
              formatter: function () {
                return "";
              },
            },
          },
          followCursor: true,
        },
        title: {
          text: this.$vuetify.lang.t("$vuetify.insights.users_counts"),
          align: "center",
          margin: 20,
          style: {
            fontSize: "20px",
            fontFamily: "Helvetica, Arial, sans-serif",
            color: "#333333",
          },
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 300,
              },
              xaxis: {
                min: 0,
                max: 100,
                labels: {
                  style: {
                    fontSize: "12px",
                  },
                },
              },
              yaxis: {
                min: 0,
                max: 100,
                labels: {
                  style: {
                    fontSize: "12px",
                  },
                },
              },
              title: {
                style: {
                  fontSize: "16px",
                },
              },
            },
          },
        ],
      },
    };
  },
  computed: {
    ...mapState({
      selectedProject: state => state.project.selectedProject,
      selectedGptProfile: state => state.project.selectedGptProfile,
      gptProfilesStore: state => state.project.gptProfiles,
      insights: state => state.insight.recommender_insights,
      gptConversationsGlobal: state => {
        return state.gpt.gpt_conversations.conversations;
      },
      gptTotalConversations: state => state.gpt.gpt_conversations.total,
      maxDate() {
        let currentDate = new Date();
        if (this.selectedProject.segment.name !== "Maira") {
          currentDate.setDate(currentDate.getDate() - 1);
        }        
        return this.formatDate(currentDate);
      },
      dateRangeText() {        
        if (this.dates.length === 1) {
          let secondDate = dateOfToday(); 
          return `${this.dates[0]} ~ ${secondDate}`;
        } else if (this.dates.length === 2) {         
          return this.dates.join(" ~ ");
        }
        return "";

      }
    }),
    ...mapGetters("loader", ["loading"]),
    overview: function () {
      return this.insights.overview;
    },
    isMobile() {
      return window.innerWidth <= 768;
    },
    datasetIdToName() {
      const map = {};
      (this.datasets || []).forEach(ds => {
        if (ds.dataset_id && ds.name && ds.dataset_id !== "new_dataset") {
          map[ds.dataset_id] = ds.name;
        }
      });
      return map;
    },
    itemChartData() {
      const { browse, purchase, ratings } = this.insights.kpi_summary || {};
      const data = [
        { x: "Browsed", y: browse?.total_browsed_items || 0 },
        { x: "Positive Rated", y: ratings?.total_positive_rated_items || 0 },
        { x: "Negative Rated", y: ratings?.total_negative_rated_items || 0 },
        { x: "Purchased", y: purchase?.total_purchased_items || 0 },
      ];
      return data.every(({ y }) => y === 0) ? [] : data;
    },
    userChartData() {
      const { browse, purchase, ratings } = this.insights.kpi_summary || {};
      const data = [
        { x: "Browsed", y: browse?.total_unique_users_browsed || 0 },
        { x: "Rated", y: ratings?.total_unique_users_rated || 0 },
        { x: "Purchased", y: purchase?.total_unique_users_purchased || 0 },
      ];
      return data.every(({ y }) => y === 0) ? [] : data;
    },
    kpiSummary: function () {
      return this.insights.kpi_summary;
    },
    deviceRatio: function () {
      return this.insights.device_ratio ?? {};
    },
    browserRatio: function () {
      return this.insights.browser_ratio ?? {};
    },
    osRatio: function () {
      return this.insights.os_ratio ?? {};
    },
    requestDetails: function () {
      return this.insights?.request_details
        ? this.insights?.request_details
        : [];
    },
    gptDetails: function () {
      let gpt_history = this.insights.gpt_history;
      if (!gpt_history) {
        gpt_history = [];
      }
      return gpt_history;
    },

    formattedPercentages() {
      return {
        percentageRecommend: this.percentageFormatted("percentageRecommend"),
        percentageSearch: this.percentageFormatted("percentageSearch"),
        percentageOrders: this.percentageFormatted("percentageOrders"),
        percentageRevenue: this.percentageFormatted("percentageRevenue"),
        percentageAvgCartSize: this.percentageFormatted(
          "percentageAvgCartSize",
        ),
        percentageAvgSaleOrder: this.percentageFormatted(
          "percentageAvgSaleOrder",
        ),
        percentageAvgSaleUser: this.percentageFormatted(
          "percentageAvgSaleUser",
        ),
        percentageAvgSaleItems: this.percentageFormatted(
          "percentageAvgSaleItems",
        ),
      };
    },
    topUsers() {
      return (
        (this.kpiSummary &&
          this.kpiSummary.purchase &&
          this.kpiSummary.purchase.top_users) ||
        []
      );
    },
    repeatedUsers() {
      return this.insights.repeat_users || [];
    },
  },
  watch: {
    dates(val) {
      if (val.length === 1) {
        this.minDate = val[0];
      } else if (val.length === 2) {
        this.minDate = null;
        this.start_date = this.dates[0];
        this.end_date = this.dates[1];
      }
    },
    conversationSearch() {
      this.debouncedSearch();
    },
    feedbackFilter() {
      this.debouncedSearch();
    },
    statusFilter() {
      this.debouncedSearch();
    },
    archiveFilter() {
      this.debouncedSearch();
    },
    selectedGptProfile(newProfile) {
      const profileId = newProfile?.id;
      if (!profileId || !this.gptProfiles?.length) return;

      if (this.gptProfilesStore?.profiles?.some(profile => profile.id === profileId)) {
        return;
      }

      const filteredProfiles = this.gptProfiles.filter(
        profile => profile.id !== profileId,
      );

      if (filteredProfiles.length === this.gptProfiles.length) return;

      this.gptProfiles = filteredProfiles;

      if (this.selectedProfileId === profileId) {
        this.selectedProfileId = null;
      }
    },
    endpointFilter() {
      this.debouncedSearch();
    },
    gptConversationHistoryOptions: {
      handler() {
        this.handlePageChange();
      },
      deep: true,
    },
    gptConversationsGlobal(newValue, _oldVal) {
      this.gptConversations = newValue;
    },
    deviceRatio(newDeviceRatio) {
      this.updateRatioOptions(
        newDeviceRatio,
        "deviceRatioOptions",
        "deviceRatioValue",
      );
    },
    browserRatio(newBrowserRatio) {
      this.updateRatioOptions(
        newBrowserRatio,
        "browserRatioOptions",
        "browserRatioValue",
      );
    },
    osRatio(newOsRatio) {
      this.updateRatioOptions(newOsRatio, "osRatioOptions", "osRatioValue");
    },
  },
  created() {
    if (this.selectedProject.segment.name !== "Maira") {
      this.getInsights();
    }else {
    // Set default date range (7 days before today)
    this.dates = this.defaultDateRange();
  }

    // Creating a debounced version of the search method
    this.debouncedSearch = debounce(this.searchConversations, 500);
    this.getGPTProfiles();
  },
  mounted() {
    this.getAllDatasets();
  },
  methods: {
    ...mapActions("insight", [
      RECOMMENDER_INSIGHTS,
      RECOMMENDER_UPDATE_FEEDBACK,
    ]),
    ...mapActions("project", [GET_GPT_DATASETS, GENERATE_CONVERSATION_HISTORY, GET_GPT_PROFILES]),
    ...mapActions("dataset", [
      CREATE_DATASET,
      ADD_CONVERSATION_TO_DATASET,
      UPLOAD_DATASET,
    ]),
    ...mapActions("gpt", [RETRIEVE_GPT_CONVERSATIONS]),
    onSessionStatusUpdated(payload) {
      this.$refs.questionnaireResponses?.applySessionStatus(payload);
    },
    capitalizeLabel(label) {
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    defaultDateRange() {
      let sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);

      return [this.formatDate(sevenDaysAgo), dateOfToday()]; // Use existing function
    },

    updateRatioOptions(ratioData, optionsKey, valueKey) {
      const labels = Object.keys(ratioData);
      const values = Object.values(ratioData);

      this[optionsKey].labels = labels.map(this.capitalizeLabel);
      this[valueKey] = values;
    },

    conversationDownloadRequest({project_id, start_date, end_date, language, feedback_type, message_seen, request_url, profile_id, search_text, is_archived}){
        return this.GENERATE_CONVERSATION_HISTORY({
          project_id,
          start_date,
          end_date,
          language,
          feedback_type,
          message_seen,
          request_url,
          profile_id,
          search_text,
          is_archived,
        })
    },
    getAllDatasets() {
      this.GET_GPT_DATASETS({
        project_id: this.selectedProject.id,
        start: 0,
        size: 999,
      })
        .then(res => {
          const filtered = res.data.datasets.filter(ds => {
            const isTimeSeries = ds.dataset_type === "time_series"
            const hasDictionaryTag = ds.tags?.includes("dictionary")

            return !isTimeSeries && !hasDictionaryTag
          })

          this.datasets = [...this.datasets, ...filtered];
        })
        .catch(e => {
          console.error("Error on getting GPT datasets", e);
        });
    },
    getLinkedDatasetNames(item) {
      const ids = item.linkedDatasets || [];
      return ids.filter(id => this.datasetIdToName[id]).map(id =>
  this.datasetIdToName[id]);
    },
    closeTrainModel() {
      this.openTrainModal = false;
    },
    handleDataset(value) {
      if (value === "new_dataset") {
        this.newDataSet = true;
        this.selectedDataset = "";
      } else {
        this.newDataSet = false;
      }
    },
    handleFileChange(file) {
      this.datasetFile = file;
    },
    createDataset() {
      if (!this.$refs.datasetForm.validate()) {
        this.showSnackBar(
          "error",
          this.$vuetify.lang.t("$vuetify.common.add_btn") +
            " " +
            this.$vuetify.lang.t("$vuetify.common.required_field"),
        );
      } else {
        const datasetRequestBody = {};
        datasetRequestBody.name = this.newDatasetName;
        datasetRequestBody.idx_column_name = this.indexColumnName;
        datasetRequestBody.is_idx_fillup_if_empty = this.fillUpEmptyIndex;

        for (const key in this.optionalDataMapper) {
          if (
            Object.prototype.hasOwnProperty.call(
              this.optionalDataMapper,
              key,
            ) &&
            this[this.optionalDataMapper[key]]
          ) {
            datasetRequestBody[key] = this[this.optionalDataMapper[key]];
          }
        }
        delete datasetRequestBody["dataset_file"];

        this.CREATE_DATASET({
          project_id: this.selectedProject.id,
          datasetRequestBody,
        })
          .then(res => {
            this.selectedDataset = res.data.detail.dataset_id;
            this.datasets.push({
              name: this.newDatasetName,
              dataset_id: res.data?.detail?.dataset_id,
              idx_column_name: this.indexColumnName,
            });
            if (this.datasetFile) {
              this.UPLOAD_DATASET({
                project_id: this.selectedProject.id,
                dataset_id: res.data?.detail?.dataset_id,
                dataset_file: this.datasetFile,
              })
                .then(_res => {
                  this.showSnackBar("success", "Dataset created successfully");
                })
                .catch(_err => {
                  this.showSnackBar("error", "Failed to upload dataset");
                });
            }
            this.newDataSet = false;
            this.showSnackBar(
              "success",
              this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_create_success"),
            );
          })
          .catch(err => {
            console.error("Error on creating dataset", err);
            this.showSnackBar(
              "error",
              this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_create_failed"),
            );
          });
      }
    },
    showSnackBar(type = "success", message = "Success") {
      this.snackbar = true;
      switch (type) {
        case "success":
          this.snackbarMessage = message;
          this.snackbarColor = "success";
          break;
        case "error":
          this.snackbarMessage = message;
          this.snackbarColor = "error";
          break;
      }
    },
    validateConversationMetadata(value) {
      try {
        JSON.parse(value);
        return true;
      } catch (error) {
        return this.$vuetify.lang.t(
          "$vuetify.gpt_history.gpt_history_conversation_metadata_invalid",
        );
      }
    },
    addTrainingData() {
      const isConversationMetadataValid = this.selectedResponseFields
        ?.conversation_metadata
        ? this.$refs.conversationMetadata.validate()
        : true;
      const isDocumentFormValid = this.$refs.documentForm.validate();
      if (!isConversationMetadataValid || !isDocumentFormValid) {
        this.showSnackBar(
          "error",
          this.$vuetify.lang.t("$vuetify.common.add_btn") +
            " " +
            this.$vuetify.lang.t("$vuetify.common.required_field"),
        );
        return;
      }
      const document = {};
      for (const key in this.selectedResponseFields) {
        if (
          Object.prototype.hasOwnProperty.call(
            this.selectedResponseFields,
            key,
          ) &&
          this.selectedResponseFields[key]
        ) {
          if (key === "conversation_metadata") {
            try {
              document[key] = JSON.parse(this.trainingData[key]);
            } catch (error) {
              return;
            }
          } else {
            document[key] = this.trainingData[key];
          }
        }
      }
      document["conversation_id"] = this.trainingData?.conversation_id;
      const is_background_task = false;
      const documents = [document];
      this.ADD_CONVERSATION_TO_DATASET({
        project_id: this.selectedProject?.id,
        dataset_id: this.selectedDataset,
        documents,
        is_background_task,
      })
        .then(_res => {
          this.openTrainModal = false;
          this.showSnackBar(
            "success",
            this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_add_document_success"),
          );
      const convId = this.trainingData?.conversation_id;
      const datasetId = this.selectedDataset;

      if (convId && datasetId) {
        this.gptConversations = this.gptConversations.map(conv => {
          if (conv.id !== convId) {
            return conv;
          }

          const existing = Array.isArray(conv.linkedDatasets)
            ? conv.linkedDatasets
            : [];

          // avoids duplicates
          if (existing.includes(datasetId)) {
            return conv;
          }

          return {
            ...conv,
            linkedDatasets: [...existing, datasetId],
          };
        });
      }
        })
        .catch(err => {
          this.showSnackBar(
            "error",
            this.$vuetify.lang.t("$vuetify.gpt_history.gpt_history_add_document_failed"),
          );
          console.error("Error on adding conversation to dataset", err);
        });
    },

    determineEndpointType(requestPath) {
      if (!requestPath) {
        return ENDPOINT_TYPES.UNKNOWN;
      }

      const path =
        typeof requestPath === "object" ? requestPath.type : requestPath;

      if (typeof path !== "string") {
        return ENDPOINT_TYPES.UNKNOWN;
      }

      return path.includes(ENDPOINT_PATHS.REALTIME)
        ? ENDPOINT_TYPES.REALTIME
        : path.includes(ENDPOINT_PATHS.VISION)
        ? ENDPOINT_TYPES.VISION
        : path.includes(ENDPOINT_PATHS.QUESTIONNAIRE)
        ? ENDPOINT_TYPES.QUESTIONNAIRE
        : path.includes(ENDPOINT_PATHS.ASK)
          ? ENDPOINT_TYPES.ASK
          : ENDPOINT_TYPES.UNKNOWN;
    },

    formatDate(date) {
      return date.toISOString().split("T")[0];
    },
    onDateChange() {
      if (this.dates.length === 1) {
        this.minDate = this.dates[0];
      }
    },

    handlePageChange() {
      let { page, itemsPerPage, sortBy, sortDesc } =
        this.gptConversationHistoryOptions;

      const sortMapping = {
        createdAt: "created_at",
        tokens: "tokens",
      };

      // Build multi-sort array
      let multiSort = {};

      if (sortBy.length) {
        sortBy.forEach((field, index) => {
          const mappedField = sortMapping[field] || "created_at";
          multiSort[mappedField] = sortDesc[index] ? "desc" : "asc";
        });
      } else {
        multiSort = { created_at: "desc" };
      }

      if (this.dates.length === 1) {
        this.dates[1] = dateOfToday();
        this.end_date = dateOfToday();
      }
      let start = 0;
      if (itemsPerPage === -1) {
        itemsPerPage = this.gptTotalConversations;
      } else {
        start = (page - 1) * itemsPerPage;
      }
      const start_date = this.dates[0];
      const end_date = this.dates[1];
      this.getGptConversationHistory(
        start_date,
        end_date,
        start,
        itemsPerPage,
        multiSort,
      );
    },
    searchConversations() {
      if (this.gptConversationHistoryOptions.page === 1) {
        this.handlePageChange();
        return;
      }
      this.gptConversationHistoryOptions.page = 1;
    },
    getGptConversationHistory(
      start_date,
      end_date,
      start = 0,
      size = 10,
      multiSort = {}
    ) {
      let project_id = this.selectedProject.id;
      this.conversationTableLoading = true;

      this.RETRIEVE_GPT_CONVERSATIONS({
        project_id: project_id,
        start_date: start_date,
        end_date: end_date,
        start: start,
        size: size,
        query: this.promptFilter,
        feedback: this.feedbackFilter,
        endpoint: this.endpointFilter,
        userId: this.userFilter,
        messageSeen: this.statusFilter,
        archiveFilter: this.archiveFilter,
        sort_by: multiSort,
        search_text: this.conversationSearch,
        gpt_profile_id: this.selectedProfileId || "",
      })
      .then(res => res)
      .catch(err => {
        console.error("Error on retrieving conversations", err);
      })
      .finally(() => {
        this.conversationTableLoading = false;
      });
    },
    getInsights() {
      this.isLoading = true;
      let project_id = this.selectedProject.id;
      if (this.dates.length === 1) {
        this.dates[1] = dateOfToday();
        this.end_date = dateOfToday();
      }
      this.RECOMMENDER_INSIGHTS({
        project_id: project_id,
        start_date: this.start_date ? this.start_date : "",
        end_date: this.end_date ? this.end_date : "",
      }).then(
        _response => {
          this.dates = [this.insights.start_date, this.insights.end_date];
          this.last_insights_updated_at = this.insights
            .last_insight_processed_time
            ? new Date(
                this.insights.last_insight_processed_time,
              ).toLocaleString(`${this.$vuetify.lang.current}-CA`, {
                timeZoneName: "short",
              })
            : "N/A";
          this.percentageAvgCartSize = this.insights.percentage_avg_cart_size;
          this.percentageAvgSaleItems = this.insights.percentage_avg_sale_items;
          this.percentageAvgSaleOrder = this.insights.percentage_avg_sale_order;
          this.percentageAvgSaleUser = this.insights.percentage_avg_sale_user;
          this.percentageOrders = this.insights.percentage_orders;
          this.percentageRecommend = this.insights.percentage_recommendations;
          this.percentageRevenue = this.insights.percentage_revenue;
          this.percentageSearch = this.insights.percentage_searches;
          this.totalNumberOfSearches = this.insights.total_number_of_searches;
          this.totalNumberOfRecommendations =
            this.insights.total_number_of_recommendations;
          this.averageCartSize = this.insights.average_cart_size;
          this.isLoading = false;
        },
        _error => {
          this.isLoading = false;
        },
      );
    },
    percentageFormatted(propertyName) {
      if (this[propertyName] === "N/A" || this[propertyName] === "---") {
        return this[propertyName];
      } else {
        return this[propertyName].toFixed(2) + "%";
      }
    },
    percentageColor(propertyName) {
      const percentage = this[propertyName];
      return percentage > 0 ? "green" : percentage < 0 ? "red" : "";
    },
    percentageIcon(propertyName) {
      const percentage = this[propertyName];
      if (isNaN(percentage) || !isFinite(percentage)) {
        return "";
      }
      return percentage > 0 ? "mdi-arrow-up" : "mdi-arrow-down";
    },
    redirectConversationDetails(item) {
      const conversation_id = item.id;
      const data = {
        project_id: this.selectedProject.id,
      };
      const parts = conversation_id.split("-");
      const secretKey = `${parts[0]}#${parts[parts.length - 1]}`;
      const urlSafeEncryptedData = encryptData(data, secretKey);
      const newTabUrl = this.$router.resolve({
        path: `/conversations/${conversation_id}`,
        query: {
          token: urlSafeEncryptedData,
          stype: this.$route.query.stype
        }
      }).href;
      window.open(newTabUrl, "_blank");
    },
    openTrainingDataModal(data) {
      this.selectedResponseFields = {
        query: true,
        response: true,
        feedback: true,
        conversation_metadata: false,
      };
      this.openTrainModal = true;
      this.trainingData = {
        conversation_id: data.id,
        query: data.requestBody.query,
        response: data.response,
        feedback: data.feedback?.message ? data.feedback?.message : "",
        conversation_metadata: data.requestBody?.conversationMetadata
          ? JSON.stringify(data.requestBody?.conversationMetadata) === "{}"
            ? ""
            : JSON.stringify(data.requestBody?.conversationMetadata)
          : "",
      };

    },
    closeTrainModal() {
      this.openTrainModal = false;
    },
    setDates() {
      this.$refs.menuDates.save(this.dates);
      if (this.selectedProject.segment.name !== "Maira") {
      this.getInsights();
    }

      if (this.dates.length === 1) {
        this.dates[1] = dateOfToday();
        this.end_date = dateOfToday();
      }
      const start_date = this.dates[0];
      const end_date = this.dates[1];
      this.start_date = start_date;
      this.end_date = end_date;
      this.getGptConversationHistory(start_date, end_date);
    },
    formatDateTime(dateTimeString) {
      // new Date(null) is the epoch, not an invalid date, so a missing time would show as 1970-01-01.
      if (!dateTimeString) return "";
      // const options = {
      //   year: "numeric",
      //   month: "2-digit",
      //   day: "2-digit",
      //   hour: "2-digit",
      //   minute: "2-digit",
      //   second: "2-digit",
      // };
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return "";
      // const formattedDate = date
      //   .toLocaleString("en-US", options)
      //   .replace(",", "");
      // Manual formatting to get the desired format: YYYY-MM-DD HH-MM-SS
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    },
    //Download button for top users
    generateFilename(reportType) {
      const startDate = this.start_date;
      const endDate = this.end_date;
      return `${this.selectedProject.id}_${reportType}_${startDate}_${endDate}.csv`;
    },
    downloadAsCSV(reportType, headers) {
      this.downloading = true;
      const filteredData =
        this.kpiSummary && this.kpiSummary.purchase
          ? this.kpiSummary.purchase.top_users
          : [];
      const filename = this.generateFilename(reportType);
      const csv = this.convertAsCSV(headers, filteredData);
      const blob = createCSVBlob(csv);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.downloading = false;
    },
    convertAsCSV(headers, data) {
      const headerValues = headers.map(header => header.value);
      const headerRow = headers.map(header => `"${header.text}"`).join(",");
      const dataRows = data.map(item =>
        headerValues
          .map(value => {
            let cellValue = item[value];
            if (
              value === "member_id" &&
              (cellValue === undefined || cellValue === "")
            ) {
              cellValue = "N/A";
            }
            return `"${(cellValue + "").replace(/"/g, '""')}"`;
          })
          .join(","),
      );
      const csvRows = [headerRow, ...dataRows];
      return csvRows.join("\n");
    },
    closeGptConversationFileGenerationModal() {
      this.gptConversationFileGenerationModal = false;
    },
    closeGptConversationHistoryModal() {
      this.gptConversationHistoryModal = false;
    },
    async getGPTProfiles() {
      try {
        if (this.loadingProfiles) return;
        this.loadingProfiles = true;

        const apiRes = await this.GET_GPT_PROFILES({
          project_id: this.$route.query.pid,
          start: this.currentPage * this.pageSize,
          size: this.pageSize,
        });
        const newProfiles = apiRes?.data?.detail?.response.profiles || [];
        const totalHits = apiRes?.data?.detail?.response?.total_hits || 0;
        this.gptProfiles = this.currentPage === 0 ? newProfiles : [...this.gptProfiles, ...newProfiles];

        this.hasMoreProfiles = this.gptProfiles.length < totalHits;
        this.currentPage++;
      } catch (e) {
        console.error("Error loading GPT profiles:", e);
      } finally {
        this.loadingProfiles = false;
      }
    },
    handleProfileChange(profileId) {
      this.selectedProfileId = profileId;
      this.searchConversations();
    },
  },
};
</script>
<style scoped>
.response-message {
  white-space: pre-line;
}

.response-details-text {
  color: gray;
  white-space: pre-line;
}

.response-message-header {
  font-size: 20px;
}

.add-btn {
  min-width: 140px;
}

@media (max-width: 959px) {
  ::v-deep .v-btn__content .v-icon--left {
    margin-left: 5px !important;
    margin-right: 5px !important;
  }
}

.btn-wrapper {
  display: flex;
  justify-content: flex-end;
}

.training-header {
  display: flex;
  justify-content: center;
  font-weight: 600;
}

.modalWrapper {
  padding: 50px 25px;
  gap: 20px;
}

.input-field {
  border: 2px solid #ccc;
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  box-sizing: border-box;
}

.textarea {
  height: 200px;
  padding: 10px;
  box-sizing: border-box;
}

.flex-grow {
  flex-grow: 1;
}

.inputWrapper {
  display: flex;
  align-items: center;
  gap: 30px;
}

.dummy-width {
  width: 17px;
}

.checkbox {
  cursor: pointer;
  transform: scale(1.5);
  margin-right: 5px;
}

.relative-pos {
  position: relative;
}

.absolute-pos {
  position: absolute;
}

.arrow-icon {
  right: 10px;
  top: 10px;
}

.text-subtitle-1 {
  font-weight: 600;
}

.conversation_id {
  font-weight: 400;
}

.required {
  color: red;
}

.response-message {
  white-space: pre-line;
}

.response-details-text {
  color: gray;
  white-space: pre-line;
}

.response-message-header {
  font-size: 20px;
}

.word-break {
  word-break: break-all;
}
</style>
<style>
.v-data-table__expanded__content {
  box-shadow: none !important;
}
</style>
